import SlDropdown from '@shoelace-style/shoelace/components/dropdown/dropdown.component';
import {notNull} from '../../../../../../../library/clxProductNet/assets/js/modules/common/assert';

export class SideNavigation {
    private readonly sideNav: HTMLElement;
    private readonly navTrigger: HTMLElement;
    private readonly navContainer: HTMLElement;
    private readonly priceSwitchDropdown: SlDropdown | null;
    private readonly mainElement: HTMLElement;
    private readonly headerElement: HTMLElement;
    private readonly activeClass: string = 'open';
    private readonly SIDENAV_BREAKPOINT = 1280;

    constructor() {
        this.sideNav = notNull(document.getElementById('side_nav') as HTMLElement);
        this.navTrigger = notNull(document.querySelector('.nav-trigger') as HTMLElement);
        this.navContainer = notNull(document.querySelector('.nav_container') as HTMLElement);
        this.priceSwitchDropdown = document.querySelector('.price-select sl-dropdown') as SlDropdown | null;
        this.mainElement = notNull(document.getElementById('main') as HTMLElement);
        this.headerElement = notNull(document.getElementById('header') as HTMLElement);

        this.initToggleBehavior();
        this.initCloseButtonBehavior();
        this.initViewportResizeObserver();
        this.applyAriaState();
        this.initSubmenuBehavior();
    }

    private initToggleBehavior(): void {
        this.navTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle();
        });
    }

    private initCloseButtonBehavior(): void {
        const closeButton = this.navContainer.querySelector<HTMLElement>('.nav_close');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.close();
            });
        }
    }

    private initViewportResizeObserver(): void {
        window.addEventListener('resize', () => {
            if (window.innerWidth >= this.SIDENAV_BREAKPOINT && this.isOpen()) {
                this.close();
            }

            if (window.innerWidth >= this.SIDENAV_BREAKPOINT) {
                this.removeOverlayElement();
            }

            this.applyAriaState();
        });
    }

    private replaceMenuContent(targetId: string, sourceSelector: string): void {
        const target = document.getElementById(targetId);
        const sourceContainer = document.querySelector(sourceSelector);
        if (!target || !sourceContainer) return;

        const temp = document.createElement('div');
        temp.innerHTML = sourceContainer.innerHTML;
        const replacement = temp.firstElementChild;

        if (replacement && target.parentNode) {
            target.parentNode.replaceChild(replacement, target);
        }
    }

    private initSubmenuBehavior(): void {
        this.replaceMenuContent('sidemenu_products', '#headmenu_products .sidemenu_entries');
        this.replaceMenuContent('sidemenu_advertising', '#headmenu_advertising .sidemenu_entries');
        this.replaceMenuContent('sidemenu_company', '#headmenu_company .sidemenu_entries');

        const listItems = this.sideNav.querySelectorAll('li');
        listItems.forEach((li) => {
            if (li.querySelector('ul')) {
                li.classList.add('parent');
                const link = li.querySelector('a');
                if (link) {
                    link.setAttribute('aria-haspopup', 'true');
                    link.setAttribute('aria-expanded', 'false');
                }
            }
        });

        const parentItems = this.sideNav.querySelectorAll('li.parent');
        parentItems.forEach((li) => {
            const submenu = li.querySelector('ul');
            if (!submenu) return;

            li.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                const clickedLink = target.closest('a');
                const isInsideSubmenu = !!target.closest('ul')?.closest('li')?.classList.contains('parent');
                const isExternalLink = clickedLink?.getAttribute('href') && clickedLink.getAttribute('href') !== '#';

                if (isInsideSubmenu && isExternalLink) {
                    return;
                }

                event.preventDefault();

                const toggleLink = li.querySelector('a');
                if (!toggleLink) return;

                if (li.classList.contains('open')) {
                    li.classList.remove('open');
                    toggleLink.setAttribute('aria-expanded', 'false');
                } else {
                    const opened = this.sideNav.querySelector('li.parent.open');
                    if (opened) {
                        opened.classList.remove('open');
                        const openLink = opened.querySelector('a');
                        if (openLink) {
                            openLink.setAttribute('aria-expanded', 'false');
                        }
                    }

                    li.classList.add('open');
                    toggleLink.setAttribute('aria-expanded', 'true');
                    submenu.removeAttribute('style');
                }
            });
        });
    }

    public isOpen(): boolean {
        return this.sideNav.classList.contains(this.activeClass);
    }

    public open(): void {
        this.sideNav.classList.add(this.activeClass);
        this.navContainer.classList.add('active');
        this.bindOverlayClick();
        this.bindEscapeKey();
        this.applyAriaState();

        const firstLink = this.sideNav.querySelector('a');
        firstLink?.focus();
    }

    public close(): void {
        this.removeOverlayElement();
        this.sideNav.classList.remove(this.activeClass);
        this.navContainer.classList.remove('active');
        this.applyAriaState();
        this.unbindEscapeKey();

        this.navTrigger.focus();
    }

    public toggle(): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    public applyAriaState(): void {
        const isMobile = window.innerWidth < this.SIDENAV_BREAKPOINT;
        const isOpen = this.isOpen();

        this.navTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        const shouldHideTrigger = !isMobile || (isMobile && isOpen);

        if (shouldHideTrigger) {
            this.navTrigger.setAttribute('aria-hidden', 'true');
            this.navTrigger.setAttribute('inert', 'true');
        } else {
            this.navTrigger.removeAttribute('aria-hidden');
            this.navTrigger.removeAttribute('inert');
        }

        if (isOpen) {
            this.sideNav.removeAttribute('inert');
            this.sideNav.removeAttribute('aria-hidden');
        } else {
            this.sideNav.setAttribute('inert', 'true');
            this.sideNav.setAttribute('aria-hidden', 'true');
        }

        this.setMainContentAccessibility(!isOpen);
    }

    private setMainContentAccessibility(visible: boolean): void {
        if (visible) {
            this.mainElement.removeAttribute('inert');
            this.mainElement.removeAttribute('aria-hidden');
            this.headerElement.removeAttribute('inert');
            this.headerElement.removeAttribute('aria-hidden');
        } else {
            this.mainElement.setAttribute('inert', 'true');
            this.mainElement.setAttribute('aria-hidden', 'true');
            this.headerElement.setAttribute('inert', 'true');
            this.headerElement.setAttribute('aria-hidden', 'true');
        }
    }

    private bindOverlayClick(): void {
        const overlay = document.querySelector<HTMLElement>('.overlay_s');
        if (!overlay || overlay.hasAttribute('data-bound')) return;

        overlay.setAttribute('data-bound', 'true');
        overlay.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
        });
    }

    private removeOverlayElement(): void {
        const overlay = document.querySelector<HTMLElement>('.overlay_s');
        if (overlay && overlay.parentElement) {
            overlay.parentElement.removeChild(overlay);
        }
    }

    private bindEscapeKey(): void {
        document.addEventListener('keydown', this.handleEscape);
    }

    private unbindEscapeKey(): void {
        document.removeEventListener('keydown', this.handleEscape);
    }

    private handleEscape = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            this.close();
        }
    };
}
